// THE template builder, one for both hosts: the trader app (a person's own
// templates) and the firm console (the organisation's). It imports nothing from
// either: who authors, how to ask a question, what to do with the form and how
// to preview it are its props.

import { createMemo, createSignal, For, Show, type JSX } from 'solid-js';
import { createStore } from 'solid-js/store';

import { builderToTemplate } from '../core/builderToTemplate';
import type { SentenceTemplate } from '../core/headlineRules';
import { moneyMark, orgWords, payoutWords, stepSentences, type OrgGovernance } from '../core/ruleText';
import { TEMPLATE_SETTLEMENT } from '../core/templateRequest';
import type { RuleBuilderState, RuleCategory } from '../core/ruleRegistry';
import type { Authority, CustomChallengeBuilderInput, StepBuilderInput } from '../form';
import AccountCard from './AccountCard';
import { emptyForm, MODE_BY_STEP_COUNT, phaseName, stepsForMode, withResets, type ChallengeMode } from './builderForm';
import { MONEY } from './HeadlineBlock';
import PhaseEditor from './PhaseEditor';
import PricingCard from './PricingCard';
import { BuilderWordsContext } from './builderWordsContext';
import StructureCard from './StructureCard';

/** A yes/no question the host asks in its own dialog. */
export interface ConfirmQuestion {
  title: string;
  message: string;
  confirmLabel: string;
}

export interface ChallengeBuilderProps {
  /** Who authors through this builder: the firm or one person. */
  authority: Authority;
  /** What the authoring org sells: the builder speaks its words. */
  governance: OrgGovernance;
  /** The firm console: say whether the template runs on an account the firm
   *  sponsors (challenge-contracts' one rule). Nothing is blocked. */
  sponsorship?: boolean;
  disabled: boolean;
  /** "Copy and edit" or "Edit": the form to open on. Read once, when the builder mounts. */
  initial?: CustomChallengeBuilderInput;
  confirm: (question: ConfirmQuestion) => Promise<boolean>;
  /** The form as authored; `templateRequest` is the request it states. */
  onSubmit: (payload: CustomChallengeBuilderInput) => void;
  /** The host's live preview of the form as a template, rendered once with
   *  accessors that follow the form. Without one, the builder lists the
   *  phase's terms in the words a trader reads. */
  preview?: (template: () => SentenceTemplate, phase: () => number) => JSX.Element;
}

export default function ChallengeBuilder(props: ChallengeBuilderProps) {
  const [mode, setMode] = createSignal<ChallengeMode>(MODE_BY_STEP_COUNT[props.initial?.steps.length ?? 2] ?? '1-phase');
  const [phase, setPhase] = createSignal(0);
  const [advancedOpen, setAdvancedOpen] = createSignal<Record<string, boolean>>({});
  // The unit an EMPTY profit-target field is waiting for, by phase. Held here
  // so moving between phases does not forget which one the author picked.
  const [targetUnits, setTargetUnits] = createSignal<Record<number, string>>({});

  // createStore — fine-grained reactivity. Updating steps[0].rules[3].value
  // only re-renders that input, not the whole form.
  const words = orgWords(props.governance);
  // The payouts of the template it writes: words that do not read its
  // settlement show no builder rather than one without them.
  const payout = payoutWords(TEMPLATE_SETTLEMENT, props.governance);
  if (payout === null) return null;
  const [form, setForm] = createStore<CustomChallengeBuilderInput>(props.initial ?? emptyForm(words));
  // A copy arrives with phases somebody authored; a fresh form earns the flag
  // on its first phase edit. Either way, replacing them asks first.
  let phasesEdited = props.initial !== undefined;
  // The currency the template is priced in: the opened request's, or the new
  // form's. No field edits it, so its mark is read once.
  const mark = moneyMark(form.currency);

  const preview = createMemo(() => builderToTemplate(form));
  const sentences = createMemo(() => {
    const step = preview().steps[phase()];
    return step ? stepSentences(preview(), step, props.governance) : [];
  });
  const sentence = (key: string) => sentences().find((line) => line.key === key)?.text ?? '';
  const step = () => form.steps[phase()];
  const targetUnit = () => targetUnits()[phase()]
    ?? (form.steps[phase()]?.profitTargetAbs.trim() ? MONEY : '%');

  async function changeMode(next: ChallengeMode) {
    if (next === mode()) return;
    const ok = !phasesEdited || await props.confirm({
      title: 'Change the structure?',
      message: 'Changing the structure resets your phases. Continue?',
      confirmLabel: 'Change it',
    });
    if (!ok) return;
    setMode(next);
    setForm('steps', stepsForMode(next));
    if (next === 'instant') setForm({ requiresActivation: false, activationPrice: '' });
    phasesEdited = false;
    setTargetUnits({});
    setPhase(0);
  }

  function update<K extends keyof CustomChallengeBuilderInput>(key: K, value: CustomChallengeBuilderInput[K]) {
    setForm(key, value);
  }

  function updateStep(partial: Partial<StepBuilderInput>) {
    phasesEdited = true;
    setForm('steps', phase(), partial);
  }

  function updateStepRules(rules: RuleBuilderState[]) {
    phasesEdited = true;
    setForm('steps', phase(), 'rules', rules);
  }

  function handleSubmit(event: Event) {
    event.preventDefault();
    // Snapshot the store into a plain object for the API call.
    props.onSubmit(JSON.parse(JSON.stringify(form)) as CustomChallengeBuilderInput);
  }

  return (
    <BuilderWordsContext.Provider value={{ org: words, payout, mark }}>
    <div class="ch-build">
      <form class="ch-build-form" onSubmit={handleSubmit}>
        <StructureCard
          name={form.displayName}
          mode={mode()}
          resetAllowed={form.resetAllowed}
          failedAccountEnd={form.failedAccountEnd}
          failedAccountDays={form.failedAccountDays}
          onName={(name) => update('displayName', name)}
          onMode={(next) => void changeMode(next)}
          onResetAllowed={(allowed) => setForm(withResets(form, allowed))}
          onFailedAccountEnd={(end) => update('failedAccountEnd', end)}
          onFailedAccountDays={(days) => update('failedAccountDays', days)}
        />
        <AccountCard form={form} onUpdate={update} />
        <PricingCard form={form} onUpdate={update} sponsorship={props.sponsorship} />

        <div class="ch-phases" role="group" aria-label="Phase">
          <For each={form.steps}>
            {(item, index) => (
              <button
                type="button"
                class="ch-phase"
                aria-pressed={phase() === index()}
                onClick={() => setPhase(index())}
              >
                {phaseName(item, index(), form.steps.length)}
              </button>
            )}
          </For>
        </div>

        <Show when={step()}>
          {(current) => (
            <PhaseEditor
              authority={props.authority}
              step={current()}
              sentence={sentence}
              targetUnit={targetUnit()}
              onTargetUnit={(unit) => setTargetUnits((prev) => ({ ...prev, [phase()]: unit }))}
              isFunded={phase() === form.steps.length - 1 && form.steps.length > 1}
              onUpdateStep={updateStep}
              onUpdateRules={updateStepRules}
              advancedOpen={advancedOpen()}
              onToggleAdvanced={(key: RuleCategory) =>
                setAdvancedOpen((prev) => ({ ...prev, [key]: !prev[key] }))}
            />
          )}
        </Show>

        <div class="ch-build-actions">
          {/* One audience per role, stated — not a choice: the platform admits
              exactly one for each, so a toggle would be a false one. */}
          <p class="ch-meta">
            {props.authority === 'firm'
              ? 'Everyone in your organisation can see and start this template.'
              : 'Only you can see this template. Invite a friend to share it.'}
          </p>
          <button class="ch-btn ch-btn-primary" type="submit" disabled={props.disabled}>
            {props.disabled ? 'Saving...' : 'Save template'}
          </button>
        </div>
      </form>

      <Show
        when={props.preview}
        fallback={
          <aside class="ch-preview" aria-label="Live preview">
            <p class="ch-eyebrow">Live preview</p>
            <ul class="ch-preview-terms">
              <For each={sentences()}>{(line) => <li>{line.text}</li>}</For>
            </ul>
          </aside>
        }
      >
        {(render) => render()(preview, phase)}
      </Show>
    </div>
    </BuilderWordsContext.Provider>
  );
}
